import * as amplitude from "@amplitude/analytics-react-native"
import HelpFAB from "@common/components/chat/HelpFAB"
import IntercomChat, {
    intercomName,
} from "@common/components/chat/IntercomChat"
import { hasTutorSupportChat } from "@common/components/chat/supportChatAccess"
import ForceUpdateGuard from "@common/components/modal/ForceUpdateGuard"
import { useDisconnectFromLive } from "../../engine/live/room/useDisconnectFromLive"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { useLiveAccessRefresh } from "@common/hooks/useLiveAccessRefresh"
import { useTutorScore } from "@common/hooks/useTutorScore"
import { congratsTypes } from "@common/screens/CongratsScreen"
import { notifyBugsnag, setBugsnagUser } from "@common/utils/initBugsnag"
import { LoadingScreen } from "@common/screens/LoadingScreen"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { setIsLiveModeEnabled } from "@data/redux/actions/liveMode"
import { tutorDsGaveUp } from "@data/redux/actions/tutorDs"
import {
    putCongrats,
    putNextScreenParams,
} from "@data/redux/actions/navigation"
import {
    clearLastNotificationIdentifier,
    setLastNotificationIdentifier,
} from "@data/redux/actions/notifications"
import { authService } from "@data/services/authService"
import { groupAttributionService } from "@data/services/groupAttributionService"
import { liveService } from "@data/services/liveService"
import { skillSetsService } from "@data/services/skillSetsService"
import { staticDataService } from "@data/services/staticDataService"
import { studentsService } from "@data/services/studentsService"
import { tutorsService } from "@data/services/tutorsService"
import { hasRole } from "@data/utility/ability"
import { opensStudentShell } from "./studentShell"
import { tutorShellFor } from "./tutorShell"
import { TUTOR_SPACE_WAIT_MS } from "../../featureFlags/tutorDsFF"
import * as Device from "expo-device"
import { getFromStorage, setToStorage } from "@data/utility/asyncStorageUtils"
import { AbilityContext } from "@data/utility/Can"
import { useRoomContext } from "@livekit/components-react"
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs"
import { useFocusEffect } from "@react-navigation/native"
import { setStatusBarStyle } from "@common/systemBars/StatusBar"
import moment from "moment"
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react"
import { Dimensions, Platform } from "react-native"
import { StatusBar } from "@common/systemBars/StatusBar"
import { connect } from "react-redux"
import { useTourGuideController } from "rn-tourguide"
import {
    saveAuthToken,
    updateLastConnected,
} from "../../data/redux/actions/authTokens"
import { studentTabScreens } from "../../student/studentTabScreens"
import { tutorTabScreens } from "../../tutor/tutorTabScreens"
import { Toast } from "../components/card/Toast"
import { timezoneChangeNotice } from "../utils/timezoneChangeNotice"
import { useIntervalPersistent } from "../hooks/useIntervalPersistent"
import NotificationSubscriptionHandler from "../notifications/NotificationSubscriptionHandler"
import { theme } from "../theme/theme"
import { IS_CALLKEEP_COMPATIBLE, IS_WEB } from "../utils/mobileUtils"
import { useInterval } from "../utils/useInterval"

const Tab = createBottomTabNavigator()

const LAST_SHOWN_TIMEZONE_MODAL_KEY = "@last_shown_timezone_modal"
const COUNTDOWN_DURATION = 72 * 3600 // 72 hours

const BottomTabsNavigator = (props) => {
    const { navigation } = props
    // La bascule de l'espace élève (`opensStudentShell`). Sinon, rien ne change : mêmes crochets,
    // dans le même ordre, et le même rendu.
    const studentShell = opensStudentShell(props.user, props.isLiveMode)
    // La bascule de l'espace tuteur : un tuteur hors live dont l'interrupteur `tutor_ds` est relu
    // allumé pour CE compte a la coquille du design system (`TutorShellBridge`) ; tant qu'il n'est pas relu
    // pour lui (juste après une connexion), un chargement neutre, jamais l'ancien espace.
    const tutorShell = props.tutorShell === "on"
    const tutorDsPending = props.tutorShell === "pending"
    const tutorDsReadsForRef = useRef({ userId: null, count: 0 })
    const { disconnectFromLive } = useDisconnectFromLive()
    const room = useRoomContext()
    const [showProfilePromptModal, setShowProfilePromptModal] = useState(false)
    const lastFetchTimeRef = useRef(0)
    const profilePromptCheckedRef = useRef(false)

    // L'attente se lève par une relecture partie avec le jeton de ce compte : celle de la connexion si
    // elle est en vol, sinon la nôtre. Deux au plus par compte (une réponse partie avant la bascule
    // peut encore arriver après la première) ; au-delà, l'espace d'aujourd'hui plutôt qu'une attente
    // sans fin — le serveur répond pourtant toujours pour le compte du jeton : on le signale.
    const giveUpTutorDs = (reason) => {
        notifyBugsnag(new Error(`tutor_ds: ${reason}`))
        props.tutorDsGaveUp(props.user?.id)
    }
    useEffect(() => {
        if (!tutorDsPending || props.staticDataRequesting) return
        const userId = props.user?.id
        const reads = tutorDsReadsForRef.current
        if (reads.userId !== userId) {
            reads.userId = userId
            reads.count = 0
        }
        if (reads.count >= 2) {
            giveUpTutorDs("static-data relu sans valeur pour le compte courant")
            return
        }
        reads.count += 1
        props.apiRequest(staticDataService.getStaticData)
    }, [tutorDsPending, props.staticDataRequesting, props.user?.id])
    // Une lecture qui ne revient jamais (ni l'app ni le navigateur ne posent de délai) : l'attente
    // s'arrête au bout du même délai que celle du pont des routes poussées.
    useEffect(() => {
        if (!tutorDsPending) return
        const timer = setTimeout(
            () => giveUpTutorDs("static-data sans réponse"),
            TUTOR_SPACE_WAIT_MS
        )
        return () => clearTimeout(timer)
    }, [tutorDsPending, props.user?.id])

    // Use custom hooks for tutor-specific data. Sous l'espace tuteur du design system, les relectures
    // que la coquille écoute pour tout le monde (le compte, le score) n'ouvrent pas la boîte « Erreur » de
    // l'ancien kit : l'écran qui a lancé le geste dit l'échec à sa place (« Rafraîchir » de « Ma journée »).
    const legacyErrorDialogs = !tutorShell
    const { shouldFetch: shouldFetchTutorScore, fetchTutorScore } =
        useTutorScore(props.user?.id, false, undefined, legacyErrorDialogs)

    const isTzDifferentThanServer =
        !!props.user?.timezone &&
        !!props.serverTimezone &&
        props.user?.timezone !== props.serverTimezone

    const showTimezoneModal = () => {
        if (isTzDifferentThanServer) {
            Toast.info({
                message: `Toutes les dates sont affichées dans votre fuseau horaire (${props.user?.timezone})`,
                position: "top",
            })
        }
    }
    useIntervalPersistent({
        callback: showTimezoneModal,
        delayInSec: COUNTDOWN_DURATION,
        persistKey: LAST_SHOWN_TIMEZONE_MODAL_KEY,
    })

    // The account follows the device's timezone without asking: say it once it has moved.
    const prevUserTzRef = useRef(undefined)
    useEffect(() => {
        const current = {
            userId: props.user?.id,
            timezone: props.user?.timezone,
            label: props.user?.timezone_label,
        }
        const notice = timezoneChangeNotice(prevUserTzRef.current, current)
        prevUserTzRef.current = current

        if (notice) Toast.info({ message: notice, position: "top" })
    }, [props.user?.id, props.user?.timezone])

    useApiStatus(authService.getUser, null, legacyErrorDialogs, (userData) => {
        if (!hasRole(userData, "student")) return

        // Check if we should show the annual profile customization prompt (once per session)
        if (profilePromptCheckedRef.current) return

        const lastPromptDate = userData?.profile?.last_profile_customization_at
        const oneYearAgo = moment().subtract(1, "year")
        const shouldShow =
            !lastPromptDate || moment(lastPromptDate).isBefore(oneYearAgo)

        if (shouldShow) {
            profilePromptCheckedRef.current = true
            setTimeout(() => {
                setShowProfilePromptModal(true)
                amplitude.track("profile_customization_prompt.shown")
            }, 2000)
        }
    })

    const closeProfilePrompt = () => {
        setShowProfilePromptModal(false)
        // Refresh user data to get the updated last_profile_customization_at
        props.apiRequest(authService.getUser)
    }

    useApiStatus(
        authService.championIntroViewed, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        () => {
            props.apiIdle(authService.championIntroViewed)
        } // success callback
    )

    useApiStatus(
        studentsService.getStudents, // api service
        null, // success message on toast (string or null)
        // Sous l'espace du design system, « Mes élèves » dit lui-même l'échec de sa liste (son bandeau).
        legacyErrorDialogs, // error message on toast (true) or keep it in redux state (false)
        () => {} // success callback
    )

    useEffect(() => {
        if (props?.token) {
            const tokenData = {
                email: props.user?.email,
                token: props?.token,
                userId: props.user?.id,
                displayName: props.user?.display_name,
                firstname: props.user?.firstname,
                lastname: props.user?.lastname,
                avatar: props.user?.avatar,
                roles: props.user?.roles,
                title_skill_set_id: props.user?.title_skill_set_id,
                hubspot_id: props.user?.hubspot_id,
                is_impersonation: !!props.user?.impersonation,
            }
            props.saveAuthToken(tokenData)
        }
    }, [props?.token])

    // Use Hooks to control!
    const {
        canStart = false, // a boolean indicate if you can start tour guide
        start, // a function to start the tourguide
        eventEmitter, // an object for listening some events
    } = IS_WEB ? {} : useTourGuideController()

    // Can start at mount 🎉
    // you need to wait until everything is registered 😁
    useEffect(() => {
        if (
            !IS_WEB &&
            canStart &&
            // La visite guidée montre les onglets legacy : elle n'a rien à montrer dans l'autre coquille.
            !studentShell &&
            hasRole(props.user, "student") &&
            props.user?.champion_intros_viewed_at?.zoe &&
            !props.user?.champion_intros_viewed_at?.zoe_menus_v1
        ) {
            // 👈 test if you can start otherwise nothing will happen
            start()
            amplitude.track("animation.loaded:didacticiel_zoe")
        }
    }, [canStart, props.user?.champion_intros_viewed_at?.zoe]) // 👈 don't miss it!
    const handleOnStop = () => {
        props.apiRequest(
            authService.championIntroViewed,
            {},
            { intro_name: "zoe_menus_v1" }
        )
    }

    // Update last connected time every 5 minutes (300,000 milliseconds)
    useInterval(
        () => {
            if (props.user?.email) {
                props.updateLastConnected(props?.user)
            }
        },
        5 * 60 * 1000
    )

    const isTrial =
        props.user?.subscription_state?.status === "on_generic_trial" &&
        Math.abs(moment().diff(moment(props.user?.trial_ends_at), "days")) <= 7

    useEffect(() => {
        if (
            hasRole(props.user, "student") &&
            isTrial &&
            !props.user?.trial_confirmed_notification &&
            !props.user?.champion_intros_viewed_at?.zoe
        ) {
            props.putNextScreenParams({
                name: "zoe",
            })

            const nextScreen = {
                nextScreen: "trial-school-subjects",
            }

            const congrats = []

            if (props.user?.live_sessions?.length > 0) {
                congrats.push({
                    congratsType: congratsTypes.OFFER_CHOSEN,
                    congratsData: {},
                    hideButton: true,
                })
            } else if (hasRole(props.user, "holidaycourse-student")) {
                congrats.push({
                    congratsType: congratsTypes.GENERIC,
                    congratsData: {
                        title: "Votre inscription a bien été prise en compte",
                        text: "Rendez-vous Samedi 3 février à 10h pour le Live spécial",
                    },
                })
            } else {
                congrats.push({
                    congratsType: congratsTypes.GENERIC,
                    congratsData: {
                        title: "Votre demande a bien été prise en compte",
                        text: "Nous vous contacterons dans les plus brefs délais pour vous attribuer une séance d'essai gratuit",
                    },
                })
            }

            props.putCongrats(congrats)

            setTimeout(() => {
                navigation.push("congrats", {
                    subscribe_to_trial_success: 1,
                    ...nextScreen,
                })
            }, 300)
        }
    }, [props.user?.champion_intros_viewed_at?.zoe])

    useEffect(() => {
        // clean any remaining live mode state and call service
        room?.disconnect(true)
        disconnectFromLive()

        if (IS_CALLKEEP_COMPATIBLE && props.hasPhonePermission) {
            eventEmitter.on("stop", handleOnStop)

            return () => {
                eventEmitter.off("stop", handleOnStop)
            }
        }
    }, [])

    //** ComponentDidMount
    useEffect(() => {
        if (props.user) {
            const identifyObj = new amplitude.Identify()
            identifyObj.set("roles", props.user?.roles)
            identifyObj.set(
                "subscription_state",
                props.user?.subscription_state?.status
            )
            amplitude.identify(identifyObj)
            amplitude.setUserId(props.user?.email)
            setBugsnagUser(props.user)
        }
        // La coquille du design system pose sa propre barre, au thème (contenu clair sur la nuit).
        if (!studentShell && !tutorShell && !tutorDsPending)
            setStatusBarStyle("dark")

        setTimeout(function () {
            // Always fetch data on component mount/reload, regardless of time since last call
            fetchData(true)
        }, 500)
    }, [])

    // Resolves true when the user is (being) fetched: requested now, or by a
    // call less than FETCH_DEBOUNCE ago.
    const fetchData = async (forceRefresh = false) => {
        const TWELVE_HOURS = 12 * 60 * 60 * 1000 // 12 hours in milliseconds
        const FETCH_DEBOUNCE = 2000 // 2 seconds minimum between fetches
        const currentTime = new Date().getTime()

        // Prevent double fetching within a short time period
        if (currentTime - lastFetchTimeRef.current < FETCH_DEBOUNCE) {
            return true
        }

        const lastFetchTime = await getFromStorage("lastApiFetchTime")
        // Each render re-runs the focus effect: a concurrent call may have
        // fetched while storage was being read.
        if (new Date().getTime() - lastFetchTimeRef.current < FETCH_DEBOUNCE) {
            return true
        }

        // Check if force refreshing, 12 hours have passed since the last fetch, or if there's no record of last fetch
        if (
            forceRefresh ||
            !lastFetchTime ||
            currentTime - parseInt(lastFetchTime) > TWELVE_HOURS
        ) {
            // Update the ref first to prevent parallel calls
            lastFetchTimeRef.current = currentTime

            props.apiRequest(staticDataService.getStaticData)
            props.apiRequest(authService.getUser)

            if (hasRole(props.user, "student")) {
                props.apiRequest(skillSetsService.getSkillSets)
                props.apiRequest(
                    authService.getFamilyTokens,
                    {},
                    {
                        device_name: IS_WEB
                            ? "WEB"
                            : (Device.deviceName ?? "mobile"),
                    }
                )
            } else if (hasRole(props.user, "tutor")) {
                // Only fetch group attribution proposals count - students data moved to MyStudentsTabScreen
                props.apiRequest(groupAttributionService.getProposalsCount)

                // Use hooks for tutor score
                if (shouldFetchTutorScore) {
                    fetchTutorScore()
                }

                // Fetch tutor tasks
                props.apiRequest(tutorsService.getTutorTasks, {}, {}, {})
            }

            // Update the last fetch time in storage
            await setToStorage("lastApiFetchTime", currentTime.toString())
            return true
        }
        return false
    }

    // After 12 hours away fetchData reloads everything, the user included.
    useLiveAccessRefresh(
        async () => {
            if (props.isLiveMode) return false
            props.apiRequest(liveService.getAllMyNextLiveSessions)
            if (!(await fetchData(false))) props.apiRequest(authService.getUser)
            return true
        },
        { enabled: hasRole(props.user, "student") }
    )

    // Execute API requests on initial load and when the screen comes into focus
    useFocusEffect(
        useCallback(() => {
            fetchData(false)
        }, [fetchData])
    )

    const ability = useContext(AbilityContext)

    // Build the list of tabs that the current user is allowed to see
    const tabScreens =
        (hasRole(props.user, "student")
            ? studentTabScreens(
                  props.user,
                  props.activityTemplates,
                  props.isLiveMode
              )
            : tutorTabScreens(props.user)
        )?.filter(
            (screen) => !screen?.can || ability?.can("read", screen?.can)
        ) || []

    // `studentTabScreens` a été appelé quand même : il porte des crochets (l'écoute des
    // notifications de messages). La pastille de « Messages », la coquille la lit elle-même.
    if (studentShell) {
        // `require` paresseux : pour un tuteur, rien du code de l'espace élève n'est évalué au démarrage.
        /* global require */
        const {
            StudentNavigator,
        } = require("../../ports/primary/ui/screens/student/StudentNavigator")
        // La relance annuelle du profil, sur le design system (le déclencheur est ci-dessus).
        const {
            StudentProfilePrompt,
        } = require("../../ports/primary/ui/screens/student/Account/StudentProfilePrompt")

        return (
            <>
                {Platform.OS !== "web" && <NotificationSubscriptionHandler />}
                <ForceUpdateGuard />
                {!!props.user && (
                    <>
                        <StudentNavigator />
                        <StudentProfilePrompt
                            visible={showProfilePromptModal}
                            onDismiss={closeProfilePrompt}
                        />
                    </>
                )}
            </>
        )
    }

    // `tutorTabScreens` a été appelé quand même : il porte des crochets.
    // L'attente de l'interrupteur du tuteur (un chargement neutre), puis sa coquille. Les gardes restent à
    // la même place que dans l'ancienne coquille : la fin de l'attente ne les remonte pas (une relecture
    // de `static-data` de plus, `ForceUpdateGuard` relisant à son montage).
    if (tutorDsPending || tutorShell) {
        // `require` paresseux : éteint, rien du code de l'espace tuteur n'est évalué au démarrage.
        const TutorShellBridge = tutorShell
            ? require("./TutorShellBridge").default
            : null

        return (
            <>
                {Platform.OS !== "web" && <NotificationSubscriptionHandler />}
                <ForceUpdateGuard />
                {tutorDsPending ? (
                    <LoadingScreen />
                ) : (
                    !!props.user && (
                        <TutorShellBridge
                            user={props.user}
                            tabScreens={tabScreens}
                        />
                    )
                )}
            </>
        )
    }

    if (!tabScreens || tabScreens.length === 0) {
        return null
    }

    return (
        <>
            {Platform.OS !== "web" && <NotificationSubscriptionHandler />}
            <ForceUpdateGuard />
            {!!props.user && (
                <Tab.Navigator
                    screenOptions={{
                        tabBarShowLabel: false,
                        unmountOnBlur: false,
                        ...(IS_WEB && {
                            tabBarLabelPosition: "beside-icon",
                        }),
                        tabBarStyle: {
                            backgroundColor: theme.colors.white,
                            borderTopWidth: 1,
                            borderColor: theme.colors.lightMore,
                            height: IS_WEB ? 74 : 80,
                            width: Dimensions.get("window").width,
                            paddingTop: IS_WEB ? 0 : 15,
                            alignItems: "center",
                        },
                    }}
                    shifting={tabScreens?.length > 1}
                    labeled={false}
                    sceneAnimationEnabled={false}
                    activeColor={theme.colors.white}
                    inactiveColor={theme.colors.white}
                >
                    {tabScreens.map((screen) => {
                        return (
                            <Tab.Screen
                                key={screen.name}
                                name={screen.name}
                                component={screen.component}
                                options={screen.options}
                            />
                        )
                    })}
                </Tab.Navigator>
            )}
            {/* Le tuteur écrit au support depuis son app : le même chat
                identifié et le même bouton « Aide » que les familles, sur tous
                ses onglets — jamais pendant un cours, où ni la bulle ni la
                session Intercom ne doivent tourner. */}
            {hasTutorSupportChat(props.user) && !props.isLiveMode && (
                <>
                    <IntercomChat
                        identified
                        userId={props.user.id}
                        email={props.user.email}
                        name={intercomName(props.user)}
                    />
                    <HelpFAB />
                </>
            )}
            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        getStaticDataError: state.api.getStaticData?.error,
        getUserError: state.api.getUser?.error,
        user: state.data.currentUser?.object,
        activityTemplates: state.data.staticData?.activity_templates,
        serverTimezone: state.data.staticData?.server_timezone,
        isLiveMode: state.liveMode.isLiveModeEnabled,
        hasPhonePermission: state.liveMode?.hasPhonePermission,
        lastNotificationIdentifier: state.notifications.lastIdentifier,
        token: state.data.session?.object?.token,
        tutorShell: tutorShellFor(state),
        // Lu pendant l'attente seulement : sinon chaque lecture de `static-data` redessinerait la coquille.
        staticDataRequesting:
            tutorShellFor(state) === "pending" &&
            !!state.api.getStaticData?.request,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    setLastNotificationIdentifier,
    clearLastNotificationIdentifier,
    putNextScreenParams,
    putCongrats,
    setIsLiveModeEnabled,
    saveAuthToken,
    updateLastConnected,
    tutorDsGaveUp,
}

export default connect(mapStateToProps, mapDispatchToProps)(BottomTabsNavigator)
